import React, { useState, useEffect } from 'react'
import { useGalaxyStore } from '../../../core/state/GalaxyStore'
import { SceneManager } from '../../../visualization/scene/GalaxyScene'
import './CameraControls.css'

export interface CameraControlsProps {
  className?: string
  scene?: SceneManager | null
}

export const CameraControls: React.FC<CameraControlsProps> = ({ className = '', scene }) => {
  const cameraMode = useGalaxyStore(state => state.ui.cameraMode)
  const selectedObject = useGalaxyStore(state => state.ui.selectedObject)
  const { setCameraMode } = useGalaxyStore.getState()
  const [autoQualityEnabled, setAutoQualityEnabled] = useState(false)

  const cameraPresets = [
    { value: 'orbit', label: 'Orbit', icon: '🌀', description: 'Orbit around Bitcoin Sun' },
    { value: 'free', label: 'Free', icon: '🎮', description: 'Free camera movement' },
    { value: 'cinematic', label: 'Tour', icon: '🎬', description: 'Cinematic tour mode' },
  ] as const

  const handleCameraModeChange = (mode: typeof cameraMode) => {
    setCameraMode(mode)
  }

  const resetView = () => {
    setCameraMode('orbit')
  }

  // Get initial auto quality state from effects manager
  useEffect(() => {
    if (scene) {
      const effectsManager = scene.getEffectsManager()
      if (effectsManager) {
        // Get current state - we need to check if it's enabled
        // For now, we'll assume it starts disabled (as per our config)
        setAutoQualityEnabled(false)
      }
    }
  }, [scene])

  const toggleAutoQuality = () => {
    if (scene) {
      const effectsManager = scene.getEffectsManager()
      if (effectsManager) {
        const newState = !autoQualityEnabled
        effectsManager.setAutoQualityEnabled(newState)
        setAutoQualityEnabled(newState)
      }
    }
  }

  return (
    <div className={`camera-controls ${className}`}>
      <div className="controls-main">
        {/* Camera Mode Section */}
        <div className="camera-section">
          <span className="section-label">📹 Camera:</span>
          <div className="camera-mode-list">
            {cameraPresets.map((preset) => (
              <button
                key={preset.value}
                className={`camera-mode-btn ${cameraMode === preset.value ? 'active' : ''}`}
                onClick={() => handleCameraModeChange(preset.value)}
                title={preset.description}
              >
                <span className="camera-icon">{preset.icon}</span>
                <div className="camera-info">
                  <span className="camera-label">{preset.label}</span>
                  <span className="camera-description">{preset.description}</span>
                </div>
              </button>
            ))}
            
            {/* Follow Mode Indicator */}
            {cameraMode === 'follow' && selectedObject && (
              <div className="follow-mode-indicator">
                <span className="follow-icon">🎯</span>
                <div className="follow-info">
                  <span className="follow-label">Following</span>
                  <span className="follow-description">Press F to stop</span>
                </div>
              </div>
            )}
            
            {/* Reset View button inline with camera modes */}
            <button
              className="action-btn reset"
              onClick={resetView}
              title="Reset to orbit view"
            >
              <span>🏠</span>
              <span>Reset</span>
            </button>
            
            {/* Auto Quality Toggle - inline with camera modes */}
            <button
              className={`action-btn quality-toggle ${autoQualityEnabled ? 'active' : ''}`}
              onClick={toggleAutoQuality}
              title={autoQualityEnabled ? 'Disable auto quality adjustment' : 'Enable auto quality adjustment'}
            >
              <span>{autoQualityEnabled ? '⚡' : '🔒'}</span>
              <span>{autoQualityEnabled ? 'Auto' : 'Manual'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}